'use client';

import { SimpleGrid } from '@ironhack/design-system2/components/grid';
import { Text } from '@ironhack/design-system2/components/text';
import { useSearchParams } from 'next/navigation';
import { filter } from 'ramda';
import { type ReactElement, useCallback, useEffect, useState } from 'react';

import { ArticleCard } from '@/app/components/client/article-card';
import { fetchBlogArticles } from '@/lib/datocms/client';
import { type DatoListArticle } from '@/lib/datocms/types';
import { type Language } from '@/lib/utils/language';
import { type Region } from '@/types';
import {
  ArticleGridPlaceholder,
  formatArticles,
} from '../article-grid-placeholder';
import { type QueryParams } from '../article-filters';
import { ArticlePagination } from '../article-pagination';
import { ArticleSeoScript } from '../article-seo-script';

export type Params = {
  language: Language;
  pagination?: string[];
  region: Region;
};

type Props = {
  articleButtonText: string;
  articles: DatoListArticle[];
  articlesPerPage: number;
  defaultFilters: QueryParams;
  filterMode: 'and' | 'or';
  notFoundText: string;
  params: Params;
  totalArticles: number;
};

export const ArticleGrid = (props: Props): ReactElement => {
  const {
    articleButtonText,
    articles,
    articlesPerPage,
    defaultFilters,
    filterMode,
    notFoundText,
    params,
    totalArticles,
  } = props;
  const { language, pagination, region } = params;

  const [filteredArticles, setFilteredArticles] = useState(articles);
  const [filteredArticleCount, setFilteredArticleCount] =
    useState(totalArticles);
  const [isLoading, setIsLoading] = useState(true);

  const has24ArticlesPerPage = articlesPerPage === 24;
  const showPagination = filteredArticleCount > articlesPerPage;

  const page = pagination?.[pagination?.indexOf('page') + 1] ?? '1';
  const activePage = Number.parseInt(page);

  const resetArticles = useCallback((): void => {
    setIsLoading(false);
    setFilteredArticles(articles);
    setFilteredArticleCount(totalArticles);
  }, [articles, totalArticles]);

  const fetchArticles = useCallback(
    (category: string | undefined, course: string | undefined) =>
      fetchBlogArticles(
        region,
        language,
        activePage,
        articlesPerPage,
        filter(Boolean, [category, course]) as string[],
        filterMode
      ),
    [activePage, articlesPerPage, filterMode, language, region]
  );

  const searchParams = useSearchParams();
  const category = searchParams?.get('category') || defaultFilters.category;
  const course = searchParams?.get('course') || defaultFilters.course;

  useEffect(() => {
    if (!category && !course) {
      resetArticles();
      return;
    }

    const updateArticles = async (): Promise<void> => {
      setIsLoading(true);

      const fetchedData = await fetchArticles(category, course);
      const {
        articles: fetchedArticles,
        articleCount: { count: fetchedArticleCount },
      } = fetchedData;

      setFilteredArticles(fetchedArticles);
      setFilteredArticleCount(fetchedArticleCount);
      setIsLoading(false);
    };

    void updateArticles();
  }, [category, course, fetchArticles, resetArticles]);

  const formattedArticles = formatArticles({
    articleButtonText,
    articles: filteredArticles,
    has24ArticlesPerPage,
  });

  return isLoading ? (
    <ArticleGridPlaceholder
      articles={filteredArticles}
      articlesPerPage={articlesPerPage}
      notFoundText={notFoundText}
    />
  ) : (
    <>
      <SimpleGrid
        columns={[1, null, 2, null, has24ArticlesPerPage ? 3 : 4]}
        justifyItems="center"
        spacing={[2, null, 3]}
      >
        {formattedArticles.length === 0 ? (
          <Text
            color="gray.500"
            gridColumn={['span 1', null, 'span 4']}
            textStyle={['xl', null, '3xl']}
          >
            {notFoundText}
          </Text>
        ) : (
          formattedArticles.map((article) => (
            <ArticleCard card={article} key={article.slug} />
          ))
        )}
      </SimpleGrid>

      {showPagination && (
        <ArticlePagination
          articleCount={filteredArticleCount}
          articlesPerPage={articlesPerPage}
          paginationParams={pagination}
        />
      )}
      <ArticleSeoScript articles={formattedArticles} />
    </>
  );
};
